import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { CognitoAuthResult } from '../models/auth.model';

export interface AssociateSoftwareTokenResult {
  SecretCode: string;
  Session: string;
}

export interface VerifySoftwareTokenResult {
  Status: string;
  Session: string;
}

@Injectable({ providedIn: 'root' })
export class CognitoService {
  private http = inject(HttpClient);
  private readonly endpoint = `https://cognito-idp.${environment.cognito.region}.amazonaws.com/`;

  initiateAuth(email: string, password: string): Observable<CognitoAuthResult> {
    return this.callCognito('AWSCognitoIdentityProviderService.InitiateAuth', {
      AuthFlow: 'USER_PASSWORD_AUTH',
      ClientId: environment.cognito.clientId,
      AuthParameters: {
        USERNAME: email,
        PASSWORD: password,
      },
    });
  }

  respondToNewPasswordChallenge(
    session: string,
    email: string,
    newPassword: string
  ): Observable<CognitoAuthResult> {
    return this.callCognito('AWSCognitoIdentityProviderService.RespondToAuthChallenge', {
      ChallengeName: 'NEW_PASSWORD_REQUIRED',
      ClientId: environment.cognito.clientId,
      Session: session,
      ChallengeResponses: {
        USERNAME: email,
        NEW_PASSWORD: newPassword,
      },
    });
  }

  respondToSoftwareTokenMfa(
    session: string,
    email: string,
    code: string
  ): Observable<CognitoAuthResult> {
    return this.callCognito('AWSCognitoIdentityProviderService.RespondToAuthChallenge', {
      ChallengeName: 'SOFTWARE_TOKEN_MFA',
      ClientId: environment.cognito.clientId,
      Session: session,
      ChallengeResponses: {
        USERNAME: email,
        SOFTWARE_TOKEN_MFA_CODE: code,
      },
    });
  }

  associateSoftwareToken(session: string): Observable<AssociateSoftwareTokenResult> {
    return this.http.post<AssociateSoftwareTokenResult>(
      this.endpoint,
      { Session: session },
      {
        headers: new HttpHeaders({
          'Content-Type': 'application/x-amz-json-1.1',
          'X-Amz-Target': 'AWSCognitoIdentityProviderService.AssociateSoftwareToken',
        }),
      }
    );
  }

  verifySoftwareToken(
    session: string,
    userCode: string
  ): Observable<VerifySoftwareTokenResult> {
    return this.http.post<VerifySoftwareTokenResult>(
      this.endpoint,
      {
        Session: session,
        UserCode: userCode,
        FriendlyDeviceName: 'Smart Config',
      },
      {
        headers: new HttpHeaders({
          'Content-Type': 'application/x-amz-json-1.1',
          'X-Amz-Target': 'AWSCognitoIdentityProviderService.VerifySoftwareToken',
        }),
      }
    );
  }

  respondToMfaSetupChallenge(session: string, email: string): Observable<CognitoAuthResult> {
    return this.callCognito('AWSCognitoIdentityProviderService.RespondToAuthChallenge', {
      ChallengeName: 'MFA_SETUP',
      ClientId: environment.cognito.clientId,
      Session: session,
      ChallengeResponses: {
        USERNAME: email,
      },
    });
  }

  refreshToken(refreshToken: string): Observable<CognitoAuthResult> {
    return this.callCognito('AWSCognitoIdentityProviderService.InitiateAuth', {
      AuthFlow: 'REFRESH_TOKEN_AUTH',
      ClientId: environment.cognito.clientId,
      AuthParameters: {
        REFRESH_TOKEN: refreshToken,
      },
    });
  }

  private callCognito(target: string, body: Record<string, unknown>): Observable<CognitoAuthResult> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/x-amz-json-1.1',
      'X-Amz-Target': target,
    });
    return this.http.post<CognitoAuthResult>(this.endpoint, body, { headers });
  }
}
